/**
 * Townhall — apps.leferi.com/townhall
 *
 * 매주 월요일 타운홀 자료를 연도별로 쌓는 히스토리. 타운홀 1회에 자료 1~4개(파일 또는 웹 링크).
 * 열람·등록은 전사 직원, 수정·삭제는 등록자 본인만(서버 판정 canEdit). 남의 타운홀은 미리보기·내려받기만.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import { Link } from 'react-router-dom'
import {
  ChevronLeft,
  Download,
  ExternalLink,
  File as FileIcon,
  FileText,
  Globe,
  LayoutGrid,
  List,
  Pencil,
  Plus,
  Presentation,
  Trash2,
  Upload,
  X as XIcon,
  type LucideIcon,
} from 'lucide-react'
import { ToastView, type ToastMsg } from './ConfirmationDocs'
import {
  TownhallError,
  addLink,
  createTownhall,
  deleteMaterial,
  deleteTownhall,
  fetchTownhalls,
  materialUrl,
  updateTownhall,
  uploadFile,
  type TownhallList,
  type TownhallMaterial,
  type TownhallRow,
} from './townhallApi'

const errorText = (e: unknown, fallback: string) =>
  e instanceof TownhallError ? e.message : fallback

/** 로컬 달력 기준 YYYY-MM-DD. */
function toDateValue(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** 이번 주 월요일(오늘이 월요일이면 오늘) — 등록 기본 날짜. */
function thisMonday(): string {
  const d = new Date()
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7))
  return toDateValue(d)
}

const parseDay = (date: string) => new Date(`${date}T00:00:00`)
const isMonday = (date: string) => parseDay(date).getDay() === 1

function formatSize(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

function iconFor(m: Pick<TownhallMaterial, 'kind' | 'ext'>): LucideIcon {
  if (m.kind === 'link') return Globe
  if (m.ext === 'key' || m.ext === 'ppt' || m.ext === 'pptx') return Presentation
  if (m.ext === 'pdf' || m.ext === 'doc' || m.ext === 'docx' || m.ext === 'hwp') return FileText
  return FileIcon
}

type ViewMode = 'list' | 'card'
const VIEW_KEY = 'townhall.view'

/** 고른 보기는 이 브라우저에만 기억한다 — 저장소를 못 쓰면 리스트로 시작. */
function readViewMode(): ViewMode {
  try {
    return localStorage.getItem(VIEW_KEY) === 'card' ? 'card' : 'list'
  } catch {
    return 'list'
  }
}

function saveViewMode(mode: ViewMode) {
  try {
    localStorage.setItem(VIEW_KEY, mode)
  } catch {
    // 저장 실패는 무시 — 이번 화면에서만 유지된다.
  }
}

function displayTitle(t: TownhallRow): string {
  const day = parseDay(t.date)
  return t.title ?? `${day.getMonth() + 1}월 ${day.getDate()}일 타운홀`
}

const formatMonthDay = (date: string) =>
  parseDay(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
const formatWeekday = (date: string) =>
  parseDay(date).toLocaleDateString('en-US', { weekday: 'long' })

const fileExt = (name: string) => (name.includes('.') ? name.split('.').pop()!.toLowerCase() : '')

const primaryBtn =
  'inline-flex items-center gap-s-2 rounded-r-2 bg-brand-pink px-s-4 py-s-2 lf-body-sm text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50'
const neutralBtn =
  'inline-flex items-center gap-s-2 rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-1 transition-colors duration-base ease-standard hover:border-line-strong focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50'
const iconBtn =
  'inline-flex h-11 w-11 shrink-0 items-center sm:h-8 sm:w-8 justify-center rounded-r-1 text-fg-2 transition-colors duration-base ease-standard hover:bg-surface-soft hover:text-fg-1 focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50'
const fieldClass =
  'w-full rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus'

export function TownhallPage() {
  const [data, setData] = useState<TownhallList | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [year, setYear] = useState<number | null>(null)
  const [view, setView] = useState<ViewMode>(readViewMode)
  // 'new' = 새 타운홀, TownhallRow = 수정 대상.
  const [editing, setEditing] = useState<'new' | TownhallRow | null>(null)
  const [deleting, setDeleting] = useState<TownhallRow | null>(null)
  const [toast, setToast] = useState<ToastMsg | null>(null)

  const reload = useCallback(async () => {
    try {
      setData(await fetchTownhalls())
      setLoadError(null)
    } catch (e) {
      setLoadError(errorText(e, '타운홀 자료를 불러오지 못했어요.'))
    }
  }, [])
  useEffect(() => {
    void reload()
  }, [reload])

  const years = useMemo(() => {
    const counts = new Map<number, number>()
    for (const t of data?.townhalls ?? []) counts.set(t.year, (counts.get(t.year) ?? 0) + 1)
    return [...counts.entries()].sort((a, b) => b[0] - a[0])
  }, [data])
  const activeYear = years.some(([y]) => y === year) ? year : (years[0]?.[0] ?? null)
  const rows = (data?.townhalls ?? []).filter((t) => t.year === activeYear)
  const changeView = (mode: ViewMode) => {
    setView(mode)
    saveViewMode(mode)
  }

  return (
    <div className="pt-s-7">
      <Link
        to="/?tab=leferist"
        className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:bg-surface-soft hover:text-fg-1 focus-visible:outline-none focus-visible:shadow-focus"
      >
        <ChevronLeft size={14} strokeWidth={1.5} />
        Apps
      </Link>

      <div className="mt-s-6 flex flex-wrap items-end justify-between gap-s-5">
        <div>
          <p className="lf-eyebrow">▪ Townhall · 타운홀 자료</p>
          <h1 className="mt-s-2 lf-display-2">Every Monday, on record.</h1>
          <p className="mt-s-3 max-w-[640px] lf-body text-fg-2">
            매주 월요일 타운홀 자료를 연도별로 모아둬요. 지난 자료도 날짜를 골라 쌓을 수 있어요.
          </p>
        </div>
        {data ? (
          <button type="button" className={primaryBtn} onClick={() => setEditing('new')}>
            <Plus size={16} strokeWidth={1.5} />
            Add townhall
          </button>
        ) : null}
      </div>

      {loadError ? (
        <div className="mt-s-7 border border-line-hairline bg-surface-soft p-s-5">
          <p className="lf-body text-status-danger">{loadError}</p>
          <button type="button" className={`${neutralBtn} mt-s-3`} onClick={() => void reload()}>
            Retry
          </button>
        </div>
      ) : !data ? (
        <p className="mt-s-7 lf-body text-fg-3">Loading…</p>
      ) : years.length === 0 ? (
        <div className="mt-s-7 border border-line-hairline bg-surface-soft p-s-7">
          <p className="lf-h4 text-fg-1">아직 등록된 타운홀이 없어요.</p>
          <p className="mt-s-2 lf-body-sm text-fg-2">
            Add townhall로 이번 주 자료부터 올려 보세요.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-s-7 flex flex-wrap items-end justify-between gap-s-4 border-b border-line-hairline">
            <div className="flex flex-wrap items-end gap-s-6">
              {years.map(([y, count]) => (
                <button
                  key={y}
                  type="button"
                  onClick={() => setYear(y)}
                  aria-current={y === activeYear ? 'page' : undefined}
                  className={`-mb-px border-b-2 pb-s-3 pt-s-1 lf-h4 lf-num transition-colors duration-base ease-standard focus-visible:outline-none focus-visible:shadow-focus ${
                    y === activeYear
                      ? 'border-brand-blue text-fg-1'
                      : 'border-transparent text-fg-3 hover:text-fg-2'
                  }`}
                >
                  {y}
                  <span className="ml-s-2 lf-caption text-fg-3">{count}</span>
                </button>
              ))}
            </div>
            <div className="mb-s-2 flex" role="group" aria-label="보기 방식">
              <ViewButton mode="list" active={view === 'list'} onSelect={changeView} />
              <ViewButton mode="card" active={view === 'card'} onSelect={changeView} />
            </div>
          </div>
          {view === 'card' ? (
            <ul className="mt-s-6 grid grid-cols-1 gap-s-4 sm:grid-cols-2 lg:grid-cols-3">
              {rows.map((t) => (
                <TownhallCard
                  key={t.id}
                  townhall={t}
                  onEdit={() => setEditing(t)}
                  onDelete={() => setDeleting(t)}
                />
              ))}
            </ul>
          ) : (
            <ul className="border-b border-line-hairline">
              {rows.map((t) => (
                <TownhallItem
                  key={t.id}
                  townhall={t}
                  onEdit={() => setEditing(t)}
                  onDelete={() => setDeleting(t)}
                />
              ))}
            </ul>
          )}
        </>
      )}

      {editing && data ? (
        <TownhallEditor
          key={editing === 'new' ? 'new' : editing.id}
          target={editing === 'new' ? null : editing}
          limits={data}
          takenDates={new Map(data.townhalls.map((t) => [t.date, t.canEdit]))}
          onClose={() => setEditing(null)}
          onChanged={reload}
          onSaved={(savedYear) => {
            setEditing(null)
            setYear(savedYear)
            setToast({ type: 'success', title: 'Saved', message: '타운홀 자료를 저장했어요.' })
          }}
        />
      ) : null}
      {deleting ? (
        <DeleteDialog
          townhall={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null)
            void reload()
            setToast({ type: 'success', title: 'Deleted', message: '타운홀을 삭제했어요.' })
          }}
        />
      ) : null}
      {toast ? <ToastView toast={toast} onClose={() => setToast(null)} /> : null}
    </div>
  )
}

function ViewButton({
  mode,
  active,
  onSelect,
}: {
  mode: ViewMode
  active: boolean
  onSelect: (mode: ViewMode) => void
}) {
  const Icon = mode === 'list' ? List : LayoutGrid
  const label = mode === 'list' ? 'List' : 'Cards'
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={mode === 'list' ? '리스트로 보기' : '카드로 보기'}
      onClick={() => onSelect(mode)}
      className={`-ml-px inline-flex items-center gap-s-1 border px-s-3 py-s-2 lf-caption transition-colors duration-base ease-standard first:ml-0 focus-visible:z-10 focus-visible:outline-none focus-visible:shadow-focus ${
        active
          ? 'z-10 border-brand-brown bg-brand-brown text-fg-inverse'
          : 'border-line-soft bg-surface-base text-fg-2 hover:text-fg-1'
      }`}
    >
      <Icon size={16} strokeWidth={1.5} />
      {label}
    </button>
  )
}

interface TownhallViewProps {
  townhall: TownhallRow
  onEdit: () => void
  onDelete: () => void
}

function ManageButtons({ onEdit, onDelete }: Pick<TownhallViewProps, 'onEdit' | 'onDelete'>) {
  return (
    <div className="flex shrink-0 gap-s-1">
      <button type="button" className={iconBtn} aria-label="타운홀 수정" title="Edit" onClick={onEdit}>
        <Pencil size={16} strokeWidth={1.5} />
      </button>
      <button type="button" className={iconBtn} aria-label="타운홀 삭제" title="Delete" onClick={onDelete}>
        <Trash2 size={16} strokeWidth={1.5} />
      </button>
    </div>
  )
}

/** 등록자 — 수정·삭제를 누구에게 부탁할지 알 수 있게. 내 것이면 'you'. */
function AddedBy({ townhall: t }: { townhall: TownhallRow }) {
  if (!t.canEdit && !t.createdBy) return null
  return (
    <p className="mt-s-1 truncate lf-caption text-fg-3">
      Added by {t.canEdit ? 'you' : t.createdBy!.split('@')[0]}
    </p>
  )
}

function TownhallItem({ townhall: t, onEdit, onDelete }: TownhallViewProps) {
  return (
    <li className="grid grid-cols-1 gap-s-4 border-t border-line-hairline py-s-5 first:border-t-0 sm:grid-cols-[120px_minmax(0,1fr)]">
      <div>
        <p className="lf-h2 lf-num text-fg-1">{formatMonthDay(t.date)}</p>
        <p className="mt-s-1 lf-caption text-fg-3">{formatWeekday(t.date)}</p>
      </div>
      <div className="min-w-0">
        <div className="flex items-start justify-between gap-s-3">
          <div className="min-w-0">
            <p className="lf-h4 text-fg-1">{displayTitle(t)}</p>
            <AddedBy townhall={t} />
          </div>
          {t.canEdit ? <ManageButtons onEdit={onEdit} onDelete={onDelete} /> : null}
        </div>
        <ul className="mt-s-3 flex flex-col gap-s-2">
          {t.materials.map((m) => (
            <MaterialRow key={m.id} townhallId={t.id} material={m} />
          ))}
        </ul>
      </div>
    </li>
  )
}

function TownhallCard({ townhall: t, onEdit, onDelete }: TownhallViewProps) {
  return (
    <li className="flex min-w-0 flex-col border border-line-hairline bg-surface-base p-s-5 transition-colors duration-base ease-standard hover:border-line-soft">
      <div className="flex items-start justify-between gap-s-3">
        <div>
          <p className="lf-h2 lf-num text-fg-1">{formatMonthDay(t.date)}</p>
          <p className="mt-s-1 lf-caption text-fg-3">{formatWeekday(t.date)}</p>
        </div>
        {t.canEdit ? <ManageButtons onEdit={onEdit} onDelete={onDelete} /> : null}
      </div>
      <p className="mt-s-4 lf-h4 text-fg-1">{displayTitle(t)}</p>
      <AddedBy townhall={t} />
      <ul className="mt-s-3 flex flex-col gap-s-2">
        {t.materials.map((m) => (
          <MaterialRow key={m.id} townhallId={t.id} material={m} compact />
        ))}
      </ul>
    </li>
  )
}

function MaterialRow({
  townhallId,
  material: m,
  compact = false,
}: {
  townhallId: string
  material: TownhallMaterial
  /** 카드처럼 폭이 좁을 때 — 파일 정보를 빼고 이름에 자리를 준다. */
  compact?: boolean
}) {
  const Icon = iconFor(m)
  const href = m.kind === 'link' ? (m.url ?? '') : materialUrl(townhallId, m.id)
  return (
    <li className="flex items-center gap-s-3 border border-line-hairline bg-surface-base px-s-3 py-s-2">
      <Icon size={20} strokeWidth={1.5} className="shrink-0 text-fg-2" />
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="min-w-0 flex-1 truncate lf-body-sm text-fg-1 hover:text-brand-blue focus-visible:outline-none focus-visible:shadow-focus"
      >
        {m.name}
      </a>
      {compact ? null : (
        <span className="hidden shrink-0 lf-caption lf-num text-fg-3 sm:inline">
          {m.kind === 'link'
            ? 'Web'
            : `${(m.ext ?? '').toUpperCase()} · ${formatSize(m.sizeBytes ?? 0)}`}
        </span>
      )}
      {m.kind === 'link' ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className={iconBtn} aria-label={`${m.name} 열기`} title="Open">
          <ExternalLink size={16} strokeWidth={1.5} />
        </a>
      ) : (
        <a href={materialUrl(townhallId, m.id, true)} className={iconBtn} aria-label={`${m.name} 내려받기`} title="Download">
          <Download size={16} strokeWidth={1.5} />
        </a>
      )}
    </li>
  )
}

/** 저장 전 추가 예정 자료. */
interface PendingItem {
  key: string
  name: string
  file?: File
  url?: string
  progress: number | null
  error: string | null
}

function TownhallEditor({
  target,
  limits,
  takenDates,
  onClose,
  onChanged,
  onSaved,
}: {
  target: TownhallRow | null
  limits: TownhallList
  /** 이미 등록된 날짜 → 내가 등록했는지. */
  takenDates: Map<string, boolean>
  onClose: () => void
  onChanged: () => Promise<void>
  onSaved: (year: number) => void
}) {
  const { maxMaterials, uploadLimitBytes, allowedExtensions } = limits
  // 이번 저장에서 새로 만들어졌거나 수정 중인 타운홀. 일부 업로드만 실패하면 이어서 재시도한다.
  const [saved, setSaved] = useState<TownhallRow | null>(target)
  const [date, setDate] = useState(target?.date ?? thisMonday())
  const [title, setTitle] = useState(target?.title ?? '')
  const [removed, setRemoved] = useState<string[]>([])
  const [pending, setPending] = useState<PendingItem[]>([])
  const [linkUrl, setLinkUrl] = useState('')
  const [linkName, setLinkName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const keySeq = useRef(0)

  const existing = (saved?.materials ?? []).filter((m) => !removed.includes(m.id))
  const count = existing.length + pending.length
  const full = count >= maxMaterials
  const dateTaken = date !== saved?.date && takenDates.has(date)
  const nextKey = () => `p${(keySeq.current += 1)}`

  function addFiles(files: File[]) {
    setError(null)
    const room = maxMaterials - count
    const accepted: PendingItem[] = []
    for (const file of files) {
      if (!allowedExtensions.includes(fileExt(file.name))) {
        setError(`${file.name}: 지원하지 않는 형식이에요. 웹 자료는 링크로 추가해 주세요.`)
      } else if (file.size > uploadLimitBytes) {
        setError(`${file.name}: ${formatSize(uploadLimitBytes)}까지 올릴 수 있어요.`)
      } else if (accepted.length >= room) {
        setError(`타운홀 하나에 자료는 ${maxMaterials}개까지 넣을 수 있어요.`)
      } else {
        accepted.push({ key: nextKey(), name: file.name.normalize('NFC'), file, progress: null, error: null })
      }
    }
    setPending((p) => [...p, ...accepted])
  }

  function addPendingLink() {
    const url = linkUrl.trim()
    if (!/^https?:\/\/\S+$/i.test(url)) {
      setError('http 또는 https로 시작하는 주소를 넣어 주세요.')
      return
    }
    setError(null)
    setPending((p) => [
      ...p,
      { key: nextKey(), name: linkName.trim() || url, url, progress: null, error: null },
    ])
    setLinkUrl('')
    setLinkName('')
  }

  function onDrop(e: DragEvent) {
    e.preventDefault()
    setDragOver(false)
    if (!busy) addFiles([...e.dataTransfer.files])
  }

  const patchPending = (key: string, patch: Partial<PendingItem>) =>
    setPending((p) => p.map((x) => (x.key === key ? { ...x, ...patch } : x)))

  async function save() {
    setBusy(true)
    setError(null)
    const isNew = !saved
    let current = saved
    try {
      current = current
        ? await updateTownhall(current.id, { date, title })
        : await createTownhall({ date, title })
    } catch (e) {
      setError(errorText(e, '저장하지 못했어요.'))
      setBusy(false)
      return
    }
    const id = current.id
    const stored = current.materials
    const adds = pending
    // 상한을 넘지 않을 만큼만 먼저 지우고, 나머지는 새 자료가 다 올라간 뒤에 지운다 —
    // 업로드가 실패해도 기존 자료를 잃지 않게.
    const toRemove = stored.filter((m) => removed.includes(m.id))
    const removeFirst = Math.max(0, stored.length + adds.length - maxMaterials)
    let failed = 0
    let added = 0
    try {
      for (const m of toRemove.slice(0, removeFirst)) await deleteMaterial(id, m.id)
    } catch (e) {
      setError(errorText(e, '자료를 지우지 못했어요.'))
      setBusy(false)
      return
    }
    for (const p of adds) {
      try {
        patchPending(p.key, { error: null, progress: p.file ? 0 : null })
        current = p.file
          ? await uploadFile(id, p.file, (r) => patchPending(p.key, { progress: r }))
          : await addLink(id, { url: p.url ?? '', name: p.name === p.url ? '' : p.name })
        added += 1
        setPending((list) => list.filter((x) => x.key !== p.key))
      } catch (e) {
        failed += 1
        patchPending(p.key, { progress: null, error: errorText(e, '올리지 못했어요.') })
      }
    }
    if (failed === 0) {
      try {
        for (const m of toRemove.slice(removeFirst)) await deleteMaterial(id, m.id)
      } catch (e) {
        setError(errorText(e, '자료를 지우지 못했어요.'))
        failed += 1
      }
    }
    // 새 타운홀에 자료가 하나도 안 올라갔으면 빈 타운홀을 남기지 않는다.
    if (isNew && added === 0) {
      await deleteTownhall(id).catch(() => undefined)
      setBusy(false)
      return
    }
    await onChanged()
    if (failed > 0) {
      setSaved(current)
      setRemoved((r) => r.filter((x) => !toRemove.slice(0, removeFirst).some((m) => m.id === x)))
      setBusy(false)
      return
    }
    onSaved(Number(date.slice(0, 4)))
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={target ? 'Edit townhall' : 'Add townhall'}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-brown/40 p-s-5"
    >
      <div className="my-s-7 flex w-full max-w-[640px] flex-col gap-s-5 border border-line-hairline bg-surface-base p-s-6 shadow-lf-3">
        <div className="flex items-center justify-between gap-s-3">
          <p className="lf-h3 text-fg-1">{target ? 'Edit townhall' : 'Add townhall'}</p>
          <button type="button" className={iconBtn} aria-label="닫기" disabled={busy} onClick={onClose}>
            <XIcon size={20} strokeWidth={1.5} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-s-4 sm:grid-cols-[180px_minmax(0,1fr)]">
          <label className="flex flex-col gap-s-1">
            <span className="lf-eyebrow text-fg-3">Date · 진행일</span>
            <input
              type="date"
              value={date}
              disabled={busy}
              onChange={(e) => setDate(e.target.value)}
              className={fieldClass}
            />
          </label>
          <label className="flex flex-col gap-s-1">
            <span className="lf-eyebrow text-fg-3">Title · 제목 (선택)</span>
            <input
              type="text"
              value={title}
              disabled={busy}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="비워두면 날짜로 표시돼요"
              className={fieldClass}
            />
          </label>
        </div>
        {dateTaken ? (
          <p className="-mt-s-3 lf-caption text-status-danger">
            {takenDates.get(date)
              ? '이 날짜의 타운홀이 이미 있어요. 그 타운홀을 수정해 주세요.'
              : '이 날짜의 타운홀은 다른 분이 등록했어요. 자료 추가는 등록한 분께 요청해 주세요.'}
          </p>
        ) : date && !isMonday(date) ? (
          <p className="-mt-s-3 lf-caption text-fg-3">월요일이 아닌 날짜예요. 대체 진행이었다면 그대로 저장하세요.</p>
        ) : null}

        <div className="flex flex-col gap-s-2">
          <div className="flex items-center justify-between gap-s-3">
            <span className="lf-eyebrow text-fg-3">Materials · 자료</span>
            <span className="lf-caption lf-num text-fg-3">
              {count} / {maxMaterials}
            </span>
          </div>
          {existing.map((m) => {
            const Icon = iconFor(m)
            return (
              <div key={m.id} className="flex items-center gap-s-3 border border-line-hairline px-s-3 py-s-2">
                <Icon size={20} strokeWidth={1.5} className="shrink-0 text-fg-2" />
                <span className="min-w-0 flex-1 truncate lf-body-sm text-fg-1">{m.name}</span>
                <button
                  type="button"
                  className={iconBtn}
                  disabled={busy}
                  aria-label={`${m.name} 빼기`}
                  onClick={() => setRemoved((r) => [...r, m.id])}
                >
                  <XIcon size={16} strokeWidth={1.5} />
                </button>
              </div>
            )
          })}
          {pending.map((p) => {
            const Icon = p.file ? iconFor({ kind: 'file', ext: fileExt(p.name) }) : Globe
            return (
              <div key={p.key} className="border border-line-hairline px-s-3 py-s-2">
                <div className="flex items-center gap-s-3">
                  <Icon size={20} strokeWidth={1.5} className="shrink-0 text-fg-2" />
                  <span className="min-w-0 flex-1 truncate lf-body-sm text-fg-1">{p.name}</span>
                  {p.file ? <span className="shrink-0 lf-caption lf-num text-fg-3">{formatSize(p.file.size)}</span> : null}
                  <button
                    type="button"
                    className={iconBtn}
                    disabled={busy}
                    aria-label={`${p.name} 빼기`}
                    onClick={() => setPending((l) => l.filter((x) => x.key !== p.key))}
                  >
                    <XIcon size={16} strokeWidth={1.5} />
                  </button>
                </div>
                {p.progress !== null ? (
                  <div className="mt-s-2 h-1 bg-surface-soft">
                    <div
                      className="h-full bg-brand-blue transition-[width] duration-base ease-standard"
                      style={{ width: `${Math.round(p.progress * 100)}%` }}
                    />
                  </div>
                ) : null}
                {p.error ? <p className="mt-s-1 lf-caption text-status-danger">{p.error}</p> : null}
              </div>
            )
          })}

          {!full ? (
            <>
              <div
                onDragOver={(e) => {
                  e.preventDefault()
                  setDragOver(true)
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`flex flex-wrap items-center justify-between gap-s-3 border border-dashed px-s-4 py-s-4 transition-colors duration-base ease-standard ${
                  dragOver ? 'border-brand-blue bg-surface-soft' : 'border-line-soft'
                }`}
              >
                <p className="lf-body-sm text-fg-2">PDF, Keynote, PPT 등 파일을 끌어다 놓으세요.</p>
                <button type="button" className={neutralBtn} disabled={busy} onClick={() => fileInput.current?.click()}>
                  <Upload size={16} strokeWidth={1.5} />
                  Choose files
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  hidden
                  accept={allowedExtensions.map((e) => `.${e}`).join(',')}
                  onChange={(e) => {
                    addFiles([...(e.target.files ?? [])])
                    e.target.value = ''
                  }}
                />
              </div>
              <div className="grid grid-cols-1 gap-s-2 sm:grid-cols-[minmax(0,1fr)_160px_auto]">
                <input
                  type="url"
                  value={linkUrl}
                  disabled={busy}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  aria-label="웹 자료 주소"
                  placeholder="https://… 웹 자료 주소"
                  className={fieldClass}
                />
                <input
                  type="text"
                  value={linkName}
                  disabled={busy}
                  onChange={(e) => setLinkName(e.target.value)}
                  aria-label="웹 자료 이름"
                  placeholder="이름 (선택)"
                  className={fieldClass}
                />
                <button
                  type="button"
                  className={neutralBtn}
                  disabled={busy || !linkUrl.trim()}
                  onClick={addPendingLink}
                >
                  <Globe size={16} strokeWidth={1.5} />
                  Add link
                </button>
              </div>
            </>
          ) : null}
        </div>

        {error ? <p className="lf-body-sm text-status-danger">{error}</p> : null}

        <div className="flex justify-end gap-s-2">
          <button type="button" className={neutralBtn} disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={primaryBtn}
            disabled={busy || !date || dateTaken || count === 0}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

function DeleteDialog({
  townhall,
  onClose,
  onDeleted,
}: {
  townhall: TownhallRow
  onClose: () => void
  onDeleted: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function confirm() {
    setBusy(true)
    setError(null)
    try {
      await deleteTownhall(townhall.id)
      onDeleted()
    } catch (e) {
      setError(errorText(e, '삭제하지 못했어요.'))
      setBusy(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Delete townhall"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-brown/40 p-s-5"
    >
      <div className="my-s-9 flex w-full max-w-[440px] flex-col gap-s-4 border border-line-hairline bg-surface-base p-s-6 shadow-lf-3">
        <p className="lf-h3 text-fg-1">Delete townhall</p>
        <p className="lf-body-sm text-fg-2">
          {townhall.date} 타운홀과 자료 {townhall.materials.length}개를 삭제해요. 되돌릴 수 없어요.
        </p>
        {error ? <p className="lf-body-sm text-status-danger">{error}</p> : null}
        <div className="flex justify-end gap-s-2">
          <button type="button" className={neutralBtn} disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void confirm()}
            className="inline-flex items-center gap-s-2 rounded-r-1 bg-status-danger px-s-3 py-s-2 lf-body-sm text-fg-inverse transition-opacity duration-base ease-standard hover:opacity-90 focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Trash2 size={16} strokeWidth={1.5} />
            Delete
          </button>
        </div>
      </div>
    </div>
  )
}
